// Helpers for the address search (components/AddressSearch.js): moving a MapLibre map to a
// geocoder result and building the project extent polygon around it.

const hasBox = (bbox) => Array.isArray(bbox) && bbox.length === 4 && bbox.every(Number.isFinite)
  && (bbox[0] !== bbox[2] || bbox[1] !== bbox[3]);

/** Fly a react-map-gl map ref to a geocoder result: fit its bbox, else zoom 17 on the point. */
export function flyToResult(mapRef, r, { duration = 800 } = {}) {
  const map = mapRef?.current;
  if (!map || !r) return;
  if (hasBox(r.bbox)) {
    const [w, s, e, n] = r.bbox;
    map.fitBounds([[w, s], [e, n]], { padding: 40, maxZoom: 18, duration });
  } else {
    map.flyTo({ center: [r.lon, r.lat], zoom: 17, duration });
  }
}

/** Closed GeoJSON Polygon for a [west, south, east, north] box. */
export function bboxPolygon([w, s, e, n]) {
  return { type: 'Polygon', coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]] };
}

/** Square GeoJSON Polygon about lon/lat, `halfMetres` from the centre to each side (~500 m wide by default). */
export function squarePolygon(lon, lat, halfMetres = 250) {
  const dLat = halfMetres / 111320;
  const dLon = halfMetres / (111320 * Math.max(Math.cos((lat * Math.PI) / 180), 0.01));
  return bboxPolygon([lon - dLon, lat - dLat, lon + dLon, lat + dLat]);
}

/** Project extent for a place: the geocoder bbox when it has one, else ~500 m around the point. */
export function placeExtent(place) {
  return hasBox(place.bbox) ? bboxPolygon(place.bbox) : squarePolygon(place.lon, place.lat);
}

/** The first part of a label ("2121 Geoscience Dr, Austin, TX" -> "2121 Geoscience Dr"). */
export const labelHead = (label) => String(label || '').split(',')[0].trim();

/** Readable message for a failed geocode request. */
export function geocodeError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (status === 503) return 'The address service is not available right now. Try again in a moment.';
  return e?.message || 'Address search failed';
}
